// SPDX-License-Identifier: AGPL-3.0-or-later
// The Agents side bar (design/agents-side-bar, A): which panel the open thread shows beside it, and
// how wide the person dragged it. Narrow, the panel sits between the thread and the bar; past
// FLOAT_FROM it floats above the thread instead, so the chat keeps its width underneath.

import type {FilesTarget} from '@app/features/konvo_agents/components/AgentFilesContext';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

/** Turns `from` (exclusive) to `to`; from 0 is the whole thread. */
export interface ChangesTarget {
	from: number;
	to: number;
	scope: 'turn' | 'thread';
}

export type SidePanel =
	| {kind: 'pull'}
	| {kind: 'changes'; target: ChangesTarget}
	| {kind: 'files'; target: FilesTarget}
	| {kind: 'time'}
	| {kind: 'terminals'}
	| {kind: 'browser'};

const WIDTH_KEY = 'konvo:agents:side-panel-width';
export const PANEL_MIN = 340;
export const PANEL_DEFAULT = 420;
/** From this width the panel leaves the columns and floats above the thread. */
export const FLOAT_FROM = 600;
/** Changes open at least this wide: code needs the room (it floats). */
export const CHANGES_DEFAULT = 760;
/** Files with the picked file beside the tree (design/agents-files A): wide enough to float. */
export const FILES_SPLIT = 860;
/** Terminals (design/agents-terminals A): room for the list and 80-odd columns of output, still docked. */
export const TERMINALS_DEFAULT = 500;
/** The browser opens wide enough for a phone-width page beside its controls. */
export const BROWSER_DEFAULT = 470;

function storedWidth(key: string, fallback: number): number {
	try {
		const value = Number(getProtectedLocalStorage()?.getItem(key));
		return Number.isFinite(value) && value >= PANEL_MIN ? value : fallback;
	} catch {
		return fallback;
	}
}

class AgentSidePanelStore {
	/** The thread the panel belongs to; another thread closes it. */
	threadKey: string | null = null;
	panel: SidePanel | null = null;
	/** Remembered per kind: the pull request reads well narrow, changes want room. */
	widths = {
		pull: storedWidth(`${WIDTH_KEY}:pull`, PANEL_DEFAULT),
		changes: storedWidth(`${WIDTH_KEY}:changes`, CHANGES_DEFAULT),
		time: storedWidth(`${WIDTH_KEY}:time`, PANEL_DEFAULT),
		files: storedWidth(`${WIDTH_KEY}:files`, PANEL_DEFAULT),
		terminals: storedWidth(`${WIDTH_KEY}:terminals`, TERMINALS_DEFAULT),
		browser: storedWidth(`${WIDTH_KEY}:browser`, BROWSER_DEFAULT),
	};

	constructor() {
		makeAutoObservable(this);
	}

	panelFor(threadKey: string): SidePanel | null {
		return this.threadKey === threadKey ? this.panel : null;
	}

	open(threadKey: string, panel: SidePanel): void {
		this.threadKey = threadKey;
		this.panel = panel;
	}

	/** Opens it, or closes it when the same one is already open. */
	toggle(threadKey: string, panel: SidePanel): void {
		const current = this.panelFor(threadKey);
		const same =
			current?.kind === panel.kind &&
			(panel.kind !== 'changes' ||
				(current.kind === 'changes' &&
					current.target.from === panel.target.from &&
					current.target.to === panel.target.to));
		if (same) this.close();
		else this.open(threadKey, panel);
	}

	close(): void {
		this.panel = null;
	}

	widthOf(kind: SidePanel['kind']): number {
		return this.widths[kind];
	}

	setWidth(kind: SidePanel['kind'], width: number, save = false): void {
		this.widths = {...this.widths, [kind]: Math.round(width)};
		if (!save) return;
		try {
			getProtectedLocalStorage()?.setItem(`${WIDTH_KEY}:${kind}`, String(Math.round(width)));
		} catch {
			// Not remembered this time; the width still holds for the visit.
		}
	}
}

export default new AgentSidePanelStore();
